import { type Order, ShipmentStatus } from "@/api.types.v2";

/**
 * Derives the order status from its shipments' statuses
 * This follows the same logic as the backend orderStatusComputer
 */
export function getOrderStatus(order: Order): string {
  if (!order.shipments || order.shipments.length === 0) {
    return "REGISTERED";
  }

  const statuses = order.shipments.map((s) => s.status);

  // Check for exceptions/failures
  if (
    statuses.some(
      (s) =>
        s === ShipmentStatus.EXCEPTION ||
        s === ShipmentStatus.DELIVERY_ATTEMPTED
    )
  ) {
    return "DELIVERY_FAILED";
  }

  // All cancelled
  if (statuses.every((s) => s === ShipmentStatus.CANCELLED)) {
    return "CANCELLED";
  }

  // All delivered
  if (statuses.every((s) => s === ShipmentStatus.DELIVERED)) {
    return "DELIVERED";
  }

  // All returned
  if (statuses.every((s) => s === ShipmentStatus.RETURNED)) {
    return "RETURNED";
  }

  // Any out for delivery
  if (statuses.some((s) => s === ShipmentStatus.OUT_FOR_DELIVERY)) {
    return "OUT_FOR_DELIVERY";
  }

  // Any in transit or at facility
  if (
    statuses.some(
      (s) => s === ShipmentStatus.IN_TRANSIT || s === ShipmentStatus.AT_FACILITY
    )
  ) {
    return "IN_TRANSIT";
  }

  // Any picked up
  if (statuses.some((s) => s === ShipmentStatus.PICKED_UP)) {
    return "PICKED_UP";
  }

  // Any label ready
  if (statuses.some((s) => s === ShipmentStatus.LABEL_READY)) {
    return "PICKUP_PENDING";
  }

  // Default
  return "REGISTERED";
}

/**
 * Get a display-friendly status label
 */
export function getOrderStatusLabel(status: string): string {
  return status.replace(/_/g, " ");
}

/**
 * Get payment status display info
 * Returns label and style class for badge
 */
export function getPaymentStatusDisplay(paymentStatus: string): {
  label: string;
  className: string;
  showBadge: boolean;
} {
  // Style based on payment status - always show actual enum value
  switch (paymentStatus) {
    case "PAID":
      return {
        label: paymentStatus,
        className: "bg-green-100 text-green-800 border-green-300",
        showBadge: true,
      };
    case "CREDIT_APPROVED":
      return {
        label: paymentStatus,
        className: "bg-cyan-100 text-cyan-800 border-cyan-300",
        showBadge: true,
      };
    case "PENDING":
      return {
        label: paymentStatus,
        className: "bg-yellow-100 text-yellow-800 border-yellow-300",
        showBadge: true,
      };
    case "PARTIAL":
      return {
        label: paymentStatus,
        className: "bg-orange-100 text-orange-800 border-orange-300",
        showBadge: true,
      };
    case "REFUNDED":
      return {
        label: paymentStatus,
        className: "bg-purple-100 text-purple-800 border-purple-300",
        showBadge: true,
      };
    case "PARTIAL_REFUND":
      return {
        label: paymentStatus,
        className: "bg-purple-100 text-purple-800 border-purple-300",
        showBadge: true,
      };
    case "FAILED":
      return {
        label: paymentStatus,
        className: "bg-red-100 text-red-800 border-red-300",
        showBadge: true,
      };
    case "VOIDED":
      return {
        label: paymentStatus,
        className: "bg-gray-100 text-gray-800 border-gray-300",
        showBadge: true,
      };
    default:
      return { label: paymentStatus, className: "", showBadge: true };
  }
}
